/**
 * T143 & T144: AsyncState component with loading and error handling
 * Reusable component for async operations with loading spinner and error messages
 */

import React, { ReactNode } from 'react';
import { Spin, Alert, Empty } from 'antd';
import { LoadingOutlined, ExclamationOutlined } from '@ant-design/icons';

export interface AsyncStateProps {
  loading?: boolean;
  error?: string | null;
  empty?: boolean;
  emptyMessage?: string;
  children?: ReactNode;
  onRetry?: () => void;
  spinningTip?: string;
  fullHeight?: boolean;
}

/**
 * AsyncState component handles loading, error, and empty states
 * Usage:
 * <AsyncState loading={isLoading} error={error} empty={data.length === 0}>
 *   <YourContent />
 * </AsyncState>
 */
export const AsyncState: React.FC<AsyncStateProps> = ({
  loading = false,
  error = null,
  empty = false,
  emptyMessage = 'Nenhum dado disponível',
  children,
  onRetry,
  spinningTip = 'Carregando...',
  fullHeight = true,
}) => {
  const containerClass = fullHeight ? 'flex justify-center items-center h-screen' : 'flex justify-center items-center py-12';

  if (loading) {
    return (
      <div className={containerClass}>
        <Spin
          indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
          tip={spinningTip}
          size="large"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-lg mx-auto px-4 py-8">
        <Alert
          message="Erro"
          description={error}
          type="error"
          showIcon
          icon={<ExclamationOutlined />}
          action={
            onRetry && (
              <button
                onClick={onRetry}
                className="ml-4 px-3 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100"
              >
                Tentar Novamente
              </button>
            )
          }
        />
      </div>
    );
  }

  if (empty) {
    return <Empty description={emptyMessage} />;
  }

  return <>{children}</>;
};

export default AsyncState;
